import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../firebase/AuthContext";
import { useAnalytics } from "../../../firebase/hooks/useAnalytics";
import { useLanguageSync } from "../../../hooks/useLanguageSync";
import { LoadingScreen } from "../../../components/LoadingScreen";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useImageUpload } from "./useImageUpload";
import { usePasswordChange } from "./usePasswordChange";
import {
    AlertBanner,
    ProfilePictureCard,
    PersonalInfoForm,
    PasswordChangeSection,
} from "./EditProfileComponents";

export default function EditProfile() {
    const {
        currentUser,
        getUserProfile,
        updateUserProfile,
        userRoles,
        isAuthReady,
    } = useAuth();
    const { trackPageView } = useAnalytics();
    const { language, setLanguage } = useLanguageSync();
    const navigate = useNavigate();
    const profile = getUserProfile();

    // ── Local form state ──────────────────────────────────────────
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [photoURL, setPhotoURL] = useState("");
    const [loading, setLoading] = useState(false);
    const [updateMessage, setUpdateMessage] = useState("");

    // ── Custom hooks ──────────────────────────────────────────────
    const imageUpload = useImageUpload({
        currentUser,
        profile,
        updateUserProfile,
        language,
    });

    const passwordChange = usePasswordChange({
        currentUser,
        language,
    });

    // ── Effects ───────────────────────────────────────────────────
    useEffect(() => {
        trackPageView("Edit Profile");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (profile && !name && !email) {
            setName(profile.displayName || "");
            setEmail(profile.email || "");
            setPhotoURL(profile.photoURL || "");
        }
    }, [profile, name, email]);

    useEffect(() => {
        if (!currentUser) {
            navigate({ pathname: "/login", search: `?lang=${language}` });
        }
    }, [currentUser, navigate, language]);

    // Sync photoURL when image upload succeeds
    useEffect(() => {
        if (imageUpload.success && profile?.photoURL) {
            setPhotoURL(profile.photoURL);
        }
    }, [imageUpload.success, profile?.photoURL]);

    // ── Handlers ──────────────────────────────────────────────────
    const handleUpdateProfile = useCallback(async () => {
        setLoading(true);
        setUpdateMessage("");

        try {
            await updateUserProfile({
                displayName:
                    name !== profile.displayName ? name : profile.displayName,
            });

            if (
                imageUpload.selectedFile &&
                imageUpload.previewURL &&
                imageUpload.previewURL !== profile.photoURL
            ) {
                await imageUpload.handleImageUpload();
            }

            setUpdateMessage("Profile updated successfully!");
            setTimeout(() => setUpdateMessage(""), 3000);
        } catch (err) {
            console.error("Error updating profile:", err);
            setUpdateMessage("Failed to update profile.");
        } finally {
            setLoading(false);
        }
    }, [name, profile, updateUserProfile, imageUpload]);

    // ── Loading state ─────────────────────────────────────────────
    if (!currentUser || !profile || !isAuthReady) {
        return <LoadingScreen language={language} setLanguage={setLanguage} />;
    }

    // ── Main render ───────────────────────────────────────────────
    return (
        <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg font-sans text-white">
            <Sidebar />
            <LanguageSwitcher language={language} setLanguage={setLanguage} />

            <div className="flex-1 flex flex-col overflow-hidden pt-12">
                <div className="flex-1 overflow-y-auto p-8">
                    <div className="max-w-6xl mx-auto">
                        {/* Header */}
                        <div className="my-4 lg:my-8 flex flex-col items-center px-4">
                            <h1 className="text-2xl lg:text-3xl font-bold text-black dark:text-white mb-1 lg:mb-2">
                                {language === "zh"
                                    ? "編輯個人資料"
                                    : "Edit Profile"}
                            </h1>
                            <p className="text-sm lg:text-base text-slate-900 dark:text-slate-100 text-center">
                                {language === "zh"
                                    ? "更新您的個人資料和設定"
                                    : "Update your personal information and settings"}
                            </p>
                        </div>

                        {/* Alert Banners */}
                        <AlertBanner message={imageUpload.error} type="error" />
                        <AlertBanner
                            message={imageUpload.success}
                            type="success"
                        />

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Left Column */}
                            <ProfilePictureCard
                                name={name}
                                email={profile.email}
                                photoURL={photoURL}
                                previewURL={imageUpload.previewURL}
                                selectedFile={imageUpload.selectedFile}
                                uploading={imageUpload.uploading}
                                language={language}
                                onFileSelect={imageUpload.handleFileSelect}
                                onImageUpload={imageUpload.handleImageUpload}
                            />

                            {/* Right Column */}
                            <div className="lg:col-span-2 space-y-8">
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <PersonalInfoForm
                                        name={name}
                                        setName={setName}
                                        email={profile.email || email}
                                        userRoles={userRoles}
                                        updateMessage={updateMessage}
                                        loading={loading}
                                        language={language}
                                        onUpdate={handleUpdateProfile}
                                        onCancel={() => navigate(-1)}
                                    />

                                    <PasswordChangeSection
                                        showPasswordSection={
                                            passwordChange.showPasswordSection
                                        }
                                        togglePasswordSection={
                                            passwordChange.togglePasswordSection
                                        }
                                        currentPassword={
                                            passwordChange.currentPassword
                                        }
                                        setCurrentPassword={
                                            passwordChange.setCurrentPassword
                                        }
                                        newPassword={passwordChange.newPassword}
                                        setNewPassword={
                                            passwordChange.setNewPassword
                                        }
                                        confirmPassword={
                                            passwordChange.confirmPassword
                                        }
                                        setConfirmPassword={
                                            passwordChange.setConfirmPassword
                                        }
                                        passwordLoading={
                                            passwordChange.passwordLoading
                                        }
                                        passwordMessage={
                                            passwordChange.passwordMessage
                                        }
                                        language={language}
                                        onPasswordChange={
                                            passwordChange.handlePasswordChange
                                        }
                                    />
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
